import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../services/adminService';
import { backupService } from '../services/backupService';
import ConfirmDialog from '../components/ConfirmDialog';

const ROLES = ['ADMIN', 'OPERADOR', 'CONSULTA'];
const FRECUENCIAS = [
  { value: 'DIARIO',  label: 'Diario' },
  { value: 'SEMANAL', label: 'Semanal (lunes)' },
  { value: 'MENSUAL', label: 'Mensual (día 1)' },
];

const TIPO_CONFIG = {
  MANUAL:           { label: 'Manual',          color: 'bg-blue-100 text-blue-700' },
  AUTOMATICO:       { label: 'Automático',       color: 'bg-green-100 text-green-700' },
  PREVIO_RESTAURAR: { label: 'Copia previa',     color: 'bg-amber-100 text-amber-700' },
  RESTAURACION:     { label: 'Restauración',     color: 'bg-red-100 text-red-700' },
};

function fmt(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
function fmtFecha(f) {
  if (!f) return '—';
  return new Date(f).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [seccion, setSeccion] = useState('usuarios');

  // ── Usuarios ──────────────────────────────────────────────
  const [usuarios, setUsuarios]     = useState([]);
  const [formUser, setFormUser]     = useState(null);
  const [editUserId, setEditUserId] = useState(null);
  const [guardandoU, setGuardandoU] = useState(false);
  const [errorU, setErrorU]         = useState(null);
  const [showPass, setShowPass]     = useState(false);

  useEffect(() => { cargarUsuarios(); }, []);
  const cargarUsuarios = () =>
    adminService.listarUsuarios().then(r => setUsuarios(r.data)).catch(() => {});

  const abrirNuevoUser = () => {
    setFormUser({ nombre: '', email: '', password: '', rol: 'OPERADOR' });
    setEditUserId(null); setErrorU(null); setShowPass(false);
  };
  const abrirEditarUser = u => {
    setFormUser({ nombre: u.nombre, email: u.email, password: '', rol: u.rol, activo: String(u.activo) });
    setEditUserId(u.id); setErrorU(null); setShowPass(false);
  };
  const handleChangeUser = e =>
    setFormUser(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleGuardarUser = async () => {
    if (!formUser.nombre || !formUser.email) { setErrorU('Nombre y email son obligatorios'); return; }
    if (!editUserId && !formUser.password)   { setErrorU('La contraseña es obligatoria para usuarios nuevos'); return; }
    setGuardandoU(true); setErrorU(null);
    try {
      editUserId
        ? await adminService.actualizarUsuario(editUserId, formUser)
        : await adminService.crearUsuario(formUser);
      setFormUser(null); setEditUserId(null);
      cargarUsuarios();
    } catch (e) {
      setErrorU(e.response?.data?.error || 'Error al guardar');
    } finally { setGuardandoU(false); }
  };

  // ── Modal de confirmación ─────────────────────────────────
  const [confirm, setConfirm] = useState(null); // { titulo, mensaje, peligro, textOk, onConfirm }
  const pedirConfirm = (opts) => new Promise(resolve => {
    setConfirm({ ...opts, onConfirm: () => { setConfirm(null); resolve(true); }, onCancel: () => { setConfirm(null); resolve(false); } });
  });

  const handleEliminarUser = async id => {
    const ok = await pedirConfirm({ titulo: 'Eliminar usuario', mensaje: '¿Estás seguro de que quieres eliminar este usuario?', peligro: true, textOk: 'Eliminar' });
    if (!ok) return;
    await adminService.eliminarUsuario(id);
    cargarUsuarios();
  };

  // ── Backup ────────────────────────────────────────────────
  const [config, setConfig]         = useState(null);
  const [historial, setHistorial]   = useState([]);
  const [guardandoB, setGuardandoB] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [restaurando, setRestaurando] = useState(false);
  const [msgB, setMsgB]             = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const fileInputRef = useRef(null);

  const emailsActuales = (config?.emailDestino || '').split(',').map(e => e.trim()).filter(Boolean);

  const handleAgregarEmail = () => {
    const email = emailInput.trim();
    if (!email || !email.includes('@')) return;
    if (emailsActuales.includes(email)) { setEmailInput(''); return; }
    const nuevos = [...emailsActuales, email].join(',');
    setConfig(c => ({ ...c, emailDestino: nuevos }));
    setEmailInput('');
  };

  const handleEliminarEmail = (email) => {
    const nuevos = emailsActuales.filter(e => e !== email).join(',');
    setConfig(c => ({ ...c, emailDestino: nuevos }));
  };

  const cargarHistorial = () =>
    backupService.historial().then(r => setHistorial(r.data.content || [])).catch(() => {});

  useEffect(() => {
    backupService.obtenerConfig().then(r => setConfig(r.data)).catch(() => {});
    cargarHistorial();
  }, []);

  const handleChangeActivo = async (e) => {
    const activarAhora = e.target.checked;
    if (!activarAhora && config?.activo) {
      const ok = await pedirConfirm({
        titulo: 'Desactivar copias automáticas',
        mensaje: '¿Seguro que quieres desactivar las copias de seguridad automáticas?\n\nLa base de datos ya no se copiará de forma periódica hasta que vuelvas a activarlo.',
        peligro: true,
        textOk: 'Sí, desactivar',
      });
      if (!ok) return;
    }
    setConfig(c => ({ ...c, activo: activarAhora }));
  };

  const handleChangeConfig = e => {
    const { name, value } = e.target;
    setConfig(c => ({ ...c, [name]: value }));
  };

  const handleGuardarConfig = async () => {
    setGuardandoB(true); setMsgB(null);
    try {
      await backupService.guardarConfig(config);
      setMsgB({ ok: true, texto: 'Configuración guardada.' });
    } catch { setMsgB({ ok: false, texto: 'Error al guardar.' }); }
    finally { setGuardandoB(false); }
  };

  const handleEjecutar = async () => {
    const ok = await pedirConfirm({ titulo: 'Copia de seguridad manual', mensaje: '¿Ejecutar una copia de seguridad ahora?', textOk: 'Hacer copia' });
    if (!ok) return;
    setEjecutando(true); setMsgB(null);
    try {
      await backupService.ejecutarAhora();
      // El backup se ejecuta en segundo plano — esperamos y recargamos el historial
      setMsgB({ ok: true, texto: 'Copia en proceso, aparecerá en el historial en unos segundos...' });
      setTimeout(() => { cargarHistorial(); setMsgB(null); }, 20000);
    } catch { setMsgB({ ok: false, texto: 'Error al iniciar la copia.' }); }
    finally { setEjecutando(false); }
  };

  const handleDescargar = async (r) => {
    try {
      const res = await backupService.descargar(r.id);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = r.fichero?.split(/[\\/]/).pop() || `backup_${r.id}.sql.gz`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setMsgB({ ok: false, texto: 'Error al descargar la copia.' }); }
  };

  const handleEliminarRegistro = async id => {
    const ok = await pedirConfirm({
      titulo: 'Eliminar entrada del historial',
      mensaje: '¿Eliminar esta entrada del historial de copias?',
      peligro: true,
      textOk: 'Eliminar',
    });
    if (!ok) return;
    try {
      await backupService.eliminarRegistro(id);
      cargarHistorial();
    } catch { setMsgB({ ok: false, texto: 'Error al eliminar la entrada.' }); }
  };

  const handleRestaurarClick = async () => {
    const ok1 = await pedirConfirm({
      titulo: 'Restaurar base de datos',
      mensaje: 'Esta operación sobreescribirá completamente la base de datos actual.\n\nTodos los datos existentes serán reemplazados por los de la copia de seguridad.',
      peligro: true,
      textOk: 'Continuar',
    });
    if (!ok1) return;
    const ok2 = await pedirConfirm({
      titulo: 'Segunda confirmación',
      mensaje: 'Se creará una copia de seguridad automática de los datos actuales antes de restaurar.\nEsta copia previa aparecerá en el historial como "Copia previa".\n\n¿Confirmas la restauración?',
      peligro: true,
      textOk: 'Sí, restaurar ahora',
    });
    if (!ok2) return;
    fileInputRef.current?.click();
  };

  const handleRestaurarFichero = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.name.endsWith('.sql.gz')) {
      setMsgB({ ok: false, texto: 'El fichero debe ser una copia de seguridad .sql.gz generada por Mondepin.' });
      return;
    }
    setRestaurando(true); setMsgB(null);
    try {
      const r = await backupService.restaurar(file);
      const rest = r.data.restauracion;
      const previo = r.data.backupPrevio;
      const ok = rest?.estado === 'OK';
      setMsgB({ ok, texto: ok
        ? `Restauración completada desde "${file.name}". Copia previa: ${previo?.fichero?.split(/[\\/]/).pop()}`
        : `Error en la restauración: ${rest?.mensajeError}` });
      cargarHistorial();
    } catch (e) {
      setMsgB({ ok: false, texto: e.response?.data?.error || 'Error al restaurar.' });
    } finally { setRestaurando(false); }
  };


  // ── Contadores e IVA ──────────────────────────────────────
  const [contadores,    setContadores]    = useState(null);
  const [tiposIva,      setTiposIva]      = useState([]);
  const [formCont,      setFormCont]      = useState(null);
  const [guardandoC,    setGuardandoC]    = useState(false);
  const [errorC,        setErrorC]        = useState(null);
  const [msgC,          setMsgC]          = useState(null);
  const [nuevoIva,      setNuevoIva]      = useState('');
  const [guardandoIva,  setGuardandoIva]  = useState(false);
  const [errorIva,      setErrorIva]      = useState(null);

  const cargarContadores = () =>
    adminService.obtenerContadores().then(r => {
      setContadores(r.data);
      setFormCont({ albaran: r.data.albaran, factura: r.data.factura, iva: r.data.iva });
    }).catch(() => {});

  const cargarTiposIva = () =>
    adminService.listarTiposIva().then(r => setTiposIva(r.data)).catch(() => {});

  useEffect(() => {
    if (seccion === 'contadores') {
      cargarContadores();
      cargarTiposIva();
    }
  }, [seccion]); // cargarContadores y cargarTiposIva son estables (useCallback sin deps)

  const handleGuardarContadores = async () => {
    setGuardandoC(true); setErrorC(null); setMsgC(null);
    try {
      await adminService.actualizarContadores({
        albaran: Number(formCont.albaran),
        factura: Number(formCont.factura),
        iva:     Number(formCont.iva),
      });
      setMsgC({ ok: true, texto: 'Contadores guardados correctamente.' });
      cargarContadores();
    } catch (e) {
      setErrorC(e.response?.data?.error || 'Error al guardar los contadores.');
    } finally { setGuardandoC(false); }
  };

  const handleCrearIva = async () => {
    const val = parseFloat(nuevoIva);
    if (isNaN(val) || val <= 0) { setErrorIva('Introduce un porcentaje válido mayor que 0.'); return; }
    setGuardandoIva(true); setErrorIva(null);
    try {
      await adminService.crearTipoIva(val);
      setNuevoIva('');
      await cargarTiposIva();
    } catch (e) {
      setErrorIva(e.response?.data?.error || 'Error al crear el tipo de IVA.');
    } finally { setGuardandoIva(false); }
  };

  // ── Render ────────────────────────────────────────────────
  const ROL_COLOR = {
    ADMIN:    'bg-red-100 text-red-700',
    OPERADOR: 'bg-blue-100 text-blue-700',
    CONSULTA: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

        {/* Cabecera */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700 text-xl font-bold">←</button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Administración</h1>
            <p className="text-sm text-gray-500">Gestión de usuarios y copias de seguridad</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl shadow-sm p-1">
          {[
            { key: 'usuarios',    label: 'Usuarios',   labelLg: 'Usuarios y roles',    icon: '👥' },
            { key: 'backup',      label: 'Backups',    labelLg: 'Copias de seguridad',  icon: '🗄' },
            { key: 'contadores',  label: 'Contadores', labelLg: 'Contadores e IVA',     icon: '🔢' },
          ].map(t => (
            <button key={t.key} onClick={() => setSeccion(t.key)}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5
                ${seccion === t.key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}>
              <span>{t.icon}</span>
              <span className="sm:hidden text-xs leading-tight">{t.label}</span>
              <span className="hidden sm:inline">{t.labelLg}</span>
            </button>
          ))}
        </div>

        {/* ── SECCIÓN USUARIOS ── */}
        {seccion === 'usuarios' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-800">Usuarios del sistema</h2>
                {!formUser && (
                  <button onClick={abrirNuevoUser}
                    className="flex items-center gap-1.5 text-sm bg-primary-600 text-white
                               px-4 py-2 rounded-lg hover:bg-primary-700 transition font-semibold">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
                    </svg>
                    Nuevo usuario
                  </button>
                )}
              </div>

              {formUser && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
                  <p className="text-sm font-semibold text-blue-700">
                    {editUserId ? 'Editar usuario' : 'Nuevo usuario'}
                  </p>
                  {errorU && <p className="text-xs text-red-600">{errorU}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Nombre</label>
                      <input name="nombre" value={formUser.nombre} onChange={handleChangeUser}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Email</label>
                      <input name="email" type="email" value={formUser.email} onChange={handleChangeUser}
                        disabled={!!editUserId}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"/>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">
                        Contraseña {editUserId && <span className="text-gray-400">(dejar vacío para no cambiar)</span>}
                      </label>
                      <div className="relative">
                        <input name="password" type={showPass ? 'text' : 'password'}
                          value={formUser.password} onChange={handleChangeUser}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
                        <button type="button" onClick={() => setShowPass(v => !v)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Rol</label>
                      <select name="rol" value={formUser.rol} onChange={handleChangeUser}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white">
                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    {editUserId && (
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Estado</label>
                        <select name="activo" value={formUser.activo} onChange={handleChangeUser}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white">
                          <option value="true">Activo</option>
                          <option value="false">Inactivo</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={handleGuardarUser} disabled={guardandoU}
                      className="flex-1 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold
                                 py-2 rounded-lg transition disabled:opacity-50">
                      {guardandoU ? 'Guardando...' : 'Guardar'}
                    </button>
                    <button onClick={() => { setFormUser(null); setEditUserId(null); }}
                      className="flex-1 border border-gray-300 text-gray-600 text-sm py-2 rounded-lg hover:bg-gray-50 transition">
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {usuarios.map(u => (
                  <div key={u.id}
                    className="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 hover:bg-gray-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${u.activo ? 'bg-green-500' : 'bg-gray-300'}`}/>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{u.nombre}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROL_COLOR[u.rol]}`}>
                        {u.rol}
                      </span>
                      <button onClick={() => abrirEditarUser(u)} title="Editar"
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                        </svg>
                      </button>
                      <button onClick={() => handleEliminarUser(u.id)} title="Eliminar"
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── SECCIÓN BACKUP ── */}
        {seccion === 'backup' && config && (
          <div className="space-y-4">

            {msgB && (
              <div className={`px-4 py-3 rounded-xl text-sm font-medium
                ${msgB.ok ? 'bg-green-50 border border-green-200 text-green-800'
                           : 'bg-red-50 border border-red-200 text-red-700'}`}>
                {msgB.texto}
              </div>
            )}

            {/* Acciones rápidas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col gap-3">
                <div>
                  <p className="font-semibold text-gray-800">Copia de seguridad manual</p>
                  <p className="text-sm text-gray-500">Genera una copia y la guarda en el servidor. Descárgala desde el historial.</p>
                </div>
                <button onClick={handleEjecutar} disabled={ejecutando || restaurando}
                  className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white
                             font-semibold px-4 py-2.5 rounded-xl transition disabled:opacity-50 text-sm">
                  {ejecutando
                    ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"/>Ejecutando...</>
                    : <>💾 Hacer copia ahora</>
                  }
                </button>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col gap-3 border-2 border-dashed border-red-200">
                <div>
                  <p className="font-semibold text-gray-800">Restaurar copia</p>
                  <p className="text-sm text-gray-500">
                    Sube un fichero <code className="text-xs bg-gray-100 px-1 rounded">.sql.gz</code> para restaurar.
                    Se creará una copia previa automáticamente.
                  </p>
                </div>
                <button onClick={handleRestaurarClick} disabled={ejecutando || restaurando}
                  className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white
                             font-semibold px-4 py-2.5 rounded-xl transition disabled:opacity-50 text-sm">
                  {restaurando
                    ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"/>Restaurando...</>
                    : <>⚠️ Restaurar desde fichero</>
                  }
                </button>
                <input ref={fileInputRef} type="file" accept=".sql.gz" className="hidden"
                  onChange={handleRestaurarFichero}/>
              </div>
            </div>

            {/* Configuración */}
            <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-800">Programación automática</h2>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name="activo" checked={!!config.activo} onChange={handleChangeActivo}
                    className="w-4 h-4 rounded"/>
                  <span className="text-sm font-medium text-gray-700">Activado</span>
                </label>
              </div>

              {/* Notificaciones por email */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-500">Notificaciones por email</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!config.emailActivo}
                      onChange={e => setConfig(c => ({ ...c, emailActivo: e.target.checked }))}
                      className="w-4 h-4 rounded"/>
                    <span className="text-sm font-medium text-gray-700">Activadas</span>
                  </label>
                </div>

                {config.emailActivo && (
                  <div className="space-y-2">
                    {/* Chips de emails */}
                    {emailsActuales.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {emailsActuales.map(email => (
                          <span key={email}
                            className="flex items-center gap-1 bg-primary-50 text-primary-700 text-xs font-medium px-2.5 py-1 rounded-full border border-primary-200">
                            {email}
                            <button onClick={() => handleEliminarEmail(email)}
                              className="text-primary-400 hover:text-primary-700 ml-0.5 leading-none">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Input para añadir email */}
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={emailInput}
                        onChange={e => setEmailInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAgregarEmail())}
                        placeholder="nuevo@email.com"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
                      <button onClick={handleAgregarEmail}
                        className="shrink-0 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-3 py-2 rounded-lg transition">
                        Añadir
                      </button>
                    </div>
                    {emailsActuales.length === 0 && (
                      <p className="text-xs text-amber-600">Añade al menos un email para recibir notificaciones.</p>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Frecuencia</label>
                  <select name="frecuencia" value={config.frecuencia || 'DIARIO'} onChange={handleChangeConfig}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white">
                    {FRECUENCIAS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Hora (0–23 h)</label>
                  <input type="number" name="hora" min={0} max={23}
                    value={config.hora ?? 3} onChange={handleChangeConfig}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"/>
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <button onClick={handleGuardarConfig} disabled={guardandoB}
                  className="bg-primary-600 hover:bg-primary-700 text-white font-semibold
                             px-6 py-2.5 rounded-xl text-sm transition disabled:opacity-50">
                  {guardandoB ? 'Guardando...' : 'Guardar configuración'}
                </button>
              </div>
            </div>

            {/* Historial */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h2 className="font-semibold text-gray-800 mb-3">Historial de copias</h2>
              {historial.length === 0
                ? <p className="text-sm text-gray-400 text-center py-4">Sin copias registradas</p>
                : (
                  <div className="space-y-2">
                    {historial.map(r => {
                      const tipoInfo = TIPO_CONFIG[r.tipo] || { label: r.tipo || 'Manual', color: 'bg-gray-100 text-gray-600' };
                      const nombre = r.fichero?.split(/[\\/]/).pop() || '—';
                      const esBackup = r.estado === 'OK' && r.tipo !== 'RESTAURACION';
                      return (
                        <div key={r.id}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 hover:bg-gray-50">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${r.estado === 'OK' ? 'bg-green-500' : 'bg-red-500'}`}/>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tipoInfo.color}`}>
                                {tipoInfo.label}
                              </span>
                              <p className="text-sm font-medium text-gray-800 truncate">{nombre}</p>
                            </div>
                            {r.usuario && (
                              <p className="text-xs text-gray-400 mt-0.5">👤 {r.usuario}</p>
                            )}
                            {r.tipo === 'RESTAURACION' && (
                              <p className="text-xs text-gray-400 mt-0.5">Registro histórico — sin fichero descargable</p>
                            )}
                            {r.mensajeError && <p className="text-xs text-red-500 truncate mt-0.5">{r.mensajeError}</p>}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <div className="text-right mr-1">
                              <p className="text-xs text-gray-500">{fmtFecha(r.fecha)}</p>
                              <p className="text-xs text-gray-400">
                                {fmt(r.tamanyoBytes)}
                                {r.emailEnviado && <span className="ml-1 text-blue-500">✉</span>}
                              </p>
                            </div>
                            {esBackup && (
                              <button onClick={() => handleDescargar(r)} title="Descargar"
                                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                                </svg>
                              </button>
                            )}
                            <button onClick={() => handleEliminarRegistro(r.id)} title="Eliminar entrada"
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              }
            </div>
          </div>
        )}
      </div>

        {/* ── SECCIÓN CONTADORES E IVA ── */}
        {seccion === 'contadores' && (
          <div className="max-w-xl mx-auto space-y-6">

            {/* Contadores */}
            <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
              <h2 className="text-base font-semibold text-gray-800">Próximos números de serie</h2>
              <p className="text-xs text-gray-500">
                Estos valores indican el siguiente número que se asignará al confirmar un siniestro o generar una factura.
                No puedes establecer un número que ya haya sido utilizado.
              </p>

              {formCont && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Próximo Nº Albarán</label>
                    <input
                      type="number" min="1" step="1"
                      value={formCont.albaran ?? ''}
                      onChange={e => setFormCont(f => ({ ...f, albaran: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Próximo Nº Factura</label>
                    <input
                      type="number" min="1" step="1"
                      value={formCont.factura ?? ''}
                      onChange={e => setFormCont(f => ({ ...f, factura: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">IVA por defecto (%)</label>
                    <select
                      value={formCont.iva ?? ''}
                      onChange={e => setFormCont(f => ({ ...f, iva: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                      {tiposIva.map(t => (
                        <option key={t.id} value={t.porcentaje}>{t.porcentaje}%</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {errorC && (
                <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {errorC}
                </p>
              )}
              {msgC && (
                <p role="status" className={`text-sm rounded-lg px-3 py-2 ${msgC.ok ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  {msgC.texto}
                </p>
              )}

              <button
                onClick={handleGuardarContadores}
                disabled={guardandoC}
                className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition disabled:opacity-50"
              >
                {guardandoC ? 'Guardando...' : 'Guardar contadores'}
              </button>
            </div>

            {/* Tipos de IVA */}
            <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
              <h2 className="text-base font-semibold text-gray-800">Tipos de IVA disponibles</h2>

              <div className="flex flex-wrap gap-2">
                {tiposIva.map(t => (
                  <span key={t.id} className="bg-gray-100 text-gray-700 text-sm font-semibold px-3 py-1 rounded-full">
                    {t.porcentaje}%
                  </span>
                ))}
                {tiposIva.length === 0 && (
                  <p className="text-sm text-gray-400">No hay tipos de IVA definidos.</p>
                )}
              </div>

              <div className="flex items-end gap-3 pt-2 border-t border-gray-100">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Añadir nuevo IVA (%)</label>
                  <input
                    type="number" min="0" step="0.01" placeholder="Ej: 10"
                    value={nuevoIva}
                    onChange={e => { setNuevoIva(e.target.value); setErrorIva(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleCrearIva()}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
                <button
                  onClick={handleCrearIva}
                  disabled={guardandoIva || !nuevoIva}
                  className="bg-gray-700 hover:bg-gray-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition disabled:opacity-50 shrink-0"
                >
                  {guardandoIva ? '...' : 'Añadir'}
                </button>
              </div>

              {errorIva && (
                <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {errorIva}
                </p>
              )}
            </div>

          </div>
        )}

      {confirm && (
        <ConfirmDialog
          abierto
          titulo={confirm.titulo}
          mensaje={confirm.mensaje}
          peligro={confirm.peligro}
          textOk={confirm.textOk}
          onConfirm={confirm.onConfirm}
          onCancel={confirm.onCancel}
        />
      )}
    </div>
  );
}
